import React, {useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Outlet, useParams} from "react-router-dom";
import {useEntityLoader} from "../../../../hooks/entites/useEntityLoader";
import {
    PublicTransitLayerEntityName,
    PublicTransitLineEntity,
} from "../../../../api/entities/replancity_PublicTransitLine";
import {
    EditLineIcon,
    EditRouteIcon,
    LineRoutesIcon,
    RouteDepartureEditIcon,
    RouteDeparturesIcon,
    RouteStopsIcon
} from "../../../../components/icons/icons";
import EntityManagerCleaner from "../../../../components/Entities/EntityManagerCleaner";
import IconTabs, {LeftTabs} from "../../../../components/LeftPanelTabs/IconTabs";
import {isErrorResponse, isUuidString} from "../../../../utils/utils";
import {TabProps} from "@mui/material/Tab/Tab";
import mapApi from "../../../../api/mapApi";
import {removedFeaturesHighlighting, requireToHighlightFeatures} from "../../../../redux/map/map-reducer";
import {useTypedSelector} from "../../../../redux/Hooks/storeSelectors";
import {selectMapLayerIdByEntityName} from "../../../../redux/selectors/selectors";
import {useDispatch} from "react-redux";
import {EntityServiceName} from "../../../../api/enums/enums";


const TransitLine = () => {
    const entityName = PublicTransitLayerEntityName.LINES;
    const {loadData} = useEntityLoader<PublicTransitLineEntity>({entityName});

    const routesLayerId: string = useTypedSelector((state) => selectMapLayerIdByEntityName(state, PublicTransitLayerEntityName.ROUTES));

    const {lineId, routeId, departureId} = useParams();
    const existingRouteId = useMemo(() => isUuidString(routeId) ? routeId : '', [routeId]);

    const {t} = useTranslation();
    const dispatch = useDispatch();

    const tabs: TabProps<any, LeftTabs>[] = [
        {
            path: '',
            label: t('transit-lines.routes'),
            url: './',
            icon: <LineRoutesIcon width={16} height={16}/>,
            testId: 'routesTab'
        },
        {
            path: 'edit',
            label: t('transit-lines.line-edit-form'),
            url: './edit',
            icon: <EditLineIcon width={16} height={16}/>,
            testId: 'editLineTab'
        }
    ];

    if (existingRouteId) {
        tabs.push(
            {
                path: '/',
                label: t('transit-lines.route-stops'),
                url: `./${existingRouteId}/`,
                icon: <RouteStopsIcon width={16} height={16}/>,
                testId: 'routeStopsTab'
            },
            {
                path: 'edit',
                label: t('transit-lines.route-form'),
                url: `./${existingRouteId}/edit`,
                icon: <EditRouteIcon width={16} height={16}/>,
                testId: 'editRouteTab'
            },
            {
                path: 'departures',
                label: t('transit-lines.departures'),
                url: `./${existingRouteId}/departures/`,
                icon: <RouteDeparturesIcon width={16} height={16}/>,
                testId: 'departuresTab'
            },
        )
    }

    if (departureId) {
        tabs.push(
            {
                path: `${departureId}/edit`,
                label: "Departure",
                url: `./${existingRouteId}/departures/${departureId}/edit`,
                icon: <RouteDepartureEditIcon width={20} height={16}/>,
                testId: 'editDepartureTab'
            },
        )
    }


    useEffect(() => {
        (async function () {
            await loadData({entityName, entityId: lineId});

            const abortController = new AbortController();
            const resp = await mapApi.getListRelatedFeatures({
                    sourceId: lineId!,
                    sourceEntityType: entityName,
                    targetEntityType: PublicTransitLayerEntityName.ROUTES
                },
                EntityServiceName.PUBLIC_TRANSIT,
                abortController.signal);

            if (!isErrorResponse(resp)) {
                const {relatedFeatures} = resp;

                const featureProperties = relatedFeatures.map(id => ({
                    id,
                    featureStateId: id,
                    layerId: routesLayerId,
                    entityName: PublicTransitLayerEntityName.ROUTES
                }));

                dispatch(requireToHighlightFeatures({
                    featureProperties
                }));
            }
        }());
    }, [loadData, entityName, lineId, routesLayerId])

    useEffect(() => {
        return () => {
            dispatch(removedFeaturesHighlighting());
        }
    }, []);


    return (
        <>
            <IconTabs tabs={tabs}/>
            <EntityManagerCleaner entityName={entityName}>
                <Outlet/>
            </EntityManagerCleaner>
        </>
    )
}

export default TransitLine;